/**
 * A synchronous mirror of "does this tab currently hold a Supabase session?".
 *
 * `supabase.auth.getSession()` is async, but the code that needs the answer —
 * `handleSupabaseError` — is synchronous and `never`-returning, called from
 * inside dozens of query functions. So the auth listener in `supabase.ts`
 * pushes the state here and the error handler reads it without awaiting.
 *
 * Deliberately TRI-state. `null` means "not determined yet" (neither the
 * listener nor the seeding `getSession()` has resolved), and consumers treat
 * unknown as *signed in*. The direction matters: this flag exists to SUPPRESS
 * error reporting, so guessing "signed out" during the startup window would
 * hide a genuine grant or RLS regression. Guessing "signed in" costs at most a
 * duplicate Sentry event inside a window measured in milliseconds.
 *
 * This module deliberately imports nothing — it sits below `supabase.ts` and
 * `supabase-error-handler.ts` in the import graph so neither can cycle through
 * the other.
 */
let sessionPresence: boolean | null = null;

/**
 * Records the live auth state. Called from the `onAuthStateChange` listener in
 * `supabase.ts` on every event, so it tracks sign-in, sign-out and refresh
 * alike without having to enumerate event names.
 */
export function recordSessionPresence(hasSession: boolean): void {
  sessionPresence = hasSession;
}

/**
 * One-shot startup seed. Only writes while the state is still unknown, so a
 * late-resolving `getSession()` can never overwrite an auth event that has
 * already told us the truth.
 */
export function seedSessionPresence(hasSession: boolean): void {
  if (sessionPresence === null) sessionPresence = hasSession;
}

/**
 * True only when this tab is KNOWN to hold no session — i.e. its requests are
 * reaching PostgREST as the `anon` role. False while the state is unknown.
 */
export function isKnownSignedOut(): boolean {
  return sessionPresence === false;
}

/** Test seam: return the mirror to its "not determined yet" state. */
export function resetSessionPresence(): void {
  sessionPresence = null;
}
